<template lang="html">
    <div class="page" id="playerBook">
        <header class="pageHeader rounded">
            <fade-image class="darken" src="assets/headers/lessons.png" />
        </header>

        <div class="scroller2">
            <header>
                <h2>Lesson Booking</h2>
                <ul class="links">
                    <li>
                        <a class="cancel" @click.prevent="$router.back()" href="#">
                            <x-icon class="icon" />
                        </a>
                    </li>
                </ul>
            </header>

            <span class="info mb10" style="background: #15f29f" v-if="user.type == 'player'">
                Your Golf Pro has selected <strong>{{ lesson_duration }} minutes</strong> as their
                preferred lesson duration, please select this option unless otherwise instructed.
            </span>

            <form class="form3" @submit.prevent="makeBooking">
                <div class="select" v-if="user.type == 'pro'">
                    <select
                        class="button dark"
                        v-model="form.player_id"
                        :class="{ complete: form.player_id }"
                    >
                        <option value="">Add Player</option>
                        <option value="guest">Guest / Group</option>
                        <option v-for="player in players" :key="player.id" :value="player.id">
                            {{ player.first_name }}
                            {{ player.last_name }}
                        </option>
                    </select>
                    <img class="arrow" src="~@/assets/arrow-right-white-2.svg" />
                </div>

                <has-error class="info error" :form="form" field="player_id" />

                <div class="formGroup" id="guestName" v-if="form.player_id == 'guest'">
                    <input
                        v-model="form.guest_name"
                        type="text"
                        placeholder="Guest / Group name..."
                        :class="{ complete: form.guest_name }"
                    />
                </div>
                <has-error class="info error" :form="form" field="guest_name" />

                <div class="select">
                    <select
                        class="button dark"
                        v-model="form.duration"
                        @change="resetForm"
                        :class="{ complete: form.duration }"
                    >
                        <option value="">Select duration</option>
                        <option value="15">
                            15 minutes
                            {{
                                lesson_duration == "15" || !lesson_duration
                                    ? "(Preferred duration)"
                                    : ""
                            }}
                        </option>
                        <option value="30">
                            30 minutes
                            {{
                                lesson_duration == "30" || !lesson_duration
                                    ? "(Preferred duration)"
                                    : ""
                            }}
                        </option>
                        <option value="45">
                            45 minutes
                            {{
                                lesson_duration == "45" || !lesson_duration
                                    ? "(Preferred duration)"
                                    : ""
                            }}
                        </option>
                        <option value="60">
                            60 minutes
                            {{
                                lesson_duration == "60" || !lesson_duration
                                    ? "(Preferred duration)"
                                    : ""
                            }}
                        </option>
                        <option value="90">
                            90 minutes
                            {{
                                lesson_duration == "90" || !lesson_duration
                                    ? "(Preferred duration)"
                                    : ""
                            }}
                        </option>
                        <option value="120">
                            120 minutes
                            {{
                                lesson_duration == "120" || !lesson_duration
                                    ? "(Preferred duration)"
                                    : ""
                            }}
                        </option>
                    </select>
                    <img class="arrow" src="~@/assets/arrow-right-white-2.svg" />
                </div>

                <has-error class="info error" :form="form" field="duration" />

                <div class="select" v-if="user.type == 'pro' && clubs.length > 1">
                    <select
                        class="button dark"
                        v-model="form.venue"
                        :class="{ complete: form.venue }"
                    >
                        <option value="">Select venue</option>
                        <option :value="club.id" v-for="club in clubs" :key="club.id">
                            {{ club.name }}
                        </option>
                    </select>
                    <img class="arrow" src="~@/assets/arrow-right-white-2.svg" />
                </div>

                <lesson-pro-date-picker
                    class="datePicker"
                    ref="datePicker"
                    style="width: 100%; margin-bottom: 10px"
                    type="player"
                    v-model="form.date"
                    @timesAdded="addTimes"
                    @calendarOpen="form.time = []"
                    :duration="form.duration"
                />

                <has-error class="info error" :form="form" field="date" />

                <lesson-pro-time-picker
                    v-model="form.time"
                    :times="times"
                    :timePickerOpen="timePickerOpen"
                    @timePickerClosed="
                        timeChosen = true;
                        timePickerOpen = false;
                    "
                    @timePickerOpen="timePickerOpen = true"
                />

                <has-error class="info error" :form="form" field="time" />

                <button
                    type="button"
                    name="button"
                    class="button dark left"
                    @click.prevent="noteOpen = true"
                    v-if="!noteOpen"
                >
                    Add a note to this lesson
                    <img class="arrow" src="~@/assets/arrow-right-white-2.svg" />
                </button>

                <div class="formGroup" v-else>
                    <textarea
                        :class="{ selected: form.note }"
                        name="name"
                        v-model="form.note"
                        placeholder="Add a note to this lesson (optional)"
                    ></textarea>
                </div>

                <div
                    class="formGroup"
                    v-if="form.duration && form.date && timeChosen && form.player_id !== 'guest'"
                    style="margin-bottom: 10px"
                >
                    <label class="radio half" :class="{ active: form.payment_type == 'credits' }">
                        <input type="radio" v-model="form.payment_type" value="credits" />
                        Use credits
                    </label>

                    <label class="radio half" :class="{ active: form.payment_type == 'on_day' }">
                        <input type="radio" v-model="form.payment_type" value="on_day" />
                        Pay on day
                    </label>
                </div>

                <has-error class="info error" :form="form" field="credits" />

                <has-error class="info error" :form="form" field="payment_type" />

                <button
                    :disabled="form.busy"
                    style="margin-bottom: 20px"
                    class="button"
                    v-if="form.duration && form.date && timeChosen"
                    type="submit"
                    name="button"
                >
                    {{ form.busy ? "Please wait..." : "Book lesson" }}
                </button>

                <empty-placeholder v-if="form.duration && form.date && timeChosen" class="warning">
                    IMPORTANT! Once a booking has been placed a cancellation will only be accepted
                    with a minumum of 24 hours notice before the lesson is due to commence. Full
                    payment will be due for lessons cancelled after this period.
                </empty-placeholder>
            </form>
        </div>
    </div>
</template>

<script>
import EmptyPlaceholder from "@/components/App/empty-placeholder";
import FadeImage from "@/components/App/fade-image";
import LessonProDatePicker from "@/components/App/lesson-pro-date-picker";
import LessonProTimePicker from "@/components/App/lesson-pro-time-picker";
import { XIcon } from "vue-feather-icons";
import { Form, HasError } from "vform";
export default {
    name: "book",
    components: {
        EmptyPlaceholder,
        FadeImage,
        XIcon,
        HasError,
        LessonProDatePicker,
        LessonProTimePicker,
    },
    computed: {
        players() {
            return this.$store.state.players;
        },
        user() {
            return this.$store.state.user;
        },
        lesson_duration() {
            if (this.user.type == "pro") {
                return this.user.settings.lesson_duration;
            } else {
                return this.user.lesson_settings.lesson_duration;
            }
        },
    },
    created() {
        this.getClubs();

        if (this.user.type == "pro" && this.players.length == 0) {
            this.$store.dispatch("getPlayers");
        }

        if (this.$route.query.id) {
            this.form.player_id = this.$route.query.id;
        }
    },
    data() {
        return {
            times: [],
            timeChosen: false,
            timePickerOpen: false,
            noteOpen: false,
            clubs: [],
            form: new Form({
                player_id: "",
                duration: "",
                date: "",
                time: [],
                note: "",
                payment_type: "",
                credits: false,
                guest_name: "",
                venue: "",
            }),
        };
    },
    methods: {
        resetForm() {
            this.timePickerOpen = false;
            this.timeChosen = false;
            this.form.time = [];
            this.form.date = "";
            this.$refs.datePicker.resetCalendar();
        },
        getClubs() {
            this.form.get("/club").then((response) => {
                this.clubs = response.data;
            });
        },
        goToLocation($url) {
            if (this.$route.query.from) {
                this.$router.go(-1);
            } else {
                if (this.user.type == "player") {
                    this.$router.push({ name: "playerLessons" });
                } else {
                    this.$router.push($url);
                }
            }
        },
        addTimes(times) {
            // Recieved the dates from the chosen dates from lesson-pro-date-picker
            this.times = times;
        },
        makeBooking() {
            this.form.post("/booking").then((response) => {
                if (this.user.type == "player") {
                    this.$store.state.user.credits = response.data.credits;
                }

                response.data.lessons.forEach(() => {
                    //this.$store.dispatch('addPlayerLesson',lesson);
                    this.$notify({
                        text: "Your lesson has been booked",
                    });

                    if (this.user.type == "pro") {
                        this.$router.push({
                            name: "proLessonBookConfirmation",
                            params: { id: response.data.lesson.id },
                        });
                    } else {
                        this.$router.push({
                            name: "BookLessonConfirmation",
                            params: { id: response.data.lesson.id },
                        });
                    }
                });
            });
        },
    },
};
</script>

<style lang="scss" scoped>
#playerBook {
    .button {
        margin-bottom: 10px;
        &.complete {
            background: white;
            color: $green_0;
        }
        .arrow {
            position: absolute;
            right: 20px;
            top: 20px;
        }
    }

    p {
        line-height: 1.5;
        color: $green_0;
    }
    .select {
        .arrow {
            position: absolute;
            right: 20px;
            top: 20px;
        }
        .button {
            padding-left: 17px !important;
        }
    }
    p {
        font-weight: 200;
        font-size: 14px;
    }
    #guestName {
        margin-bottom: 10px;
        input[type="text"].complete {
            background: white;
        }
    }
}
</style>
